"use client";

import { formatCurrency } from "@/utils/helpers";
import { captureClientEvent } from "@/lib/telemetry-client";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripePaymentElementOptions } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { FaSpinner, FaLock } from "react-icons/fa6";

export default function CheckoutStripe({
    paymethod,
    email,
    price,
    returnPath = "/tickets/success",
}: {
    paymethod: string;
    email: string;
    price: number;
    returnPath?: string;
}) {
    const stripe = useStripe();
    const elements = useElements();

    const [message, setMessage] = useState<string | undefined>(undefined);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);
    const [isLoadingElement, setIsLoadingElement] = useState(true);
    const [elementLoadTimedOut, setElementLoadTimedOut] = useState(false);

    useEffect(() => {
        if (!isLoadingElement) {
            return;
        }
        const timeout = window.setTimeout(() => {
            setElementLoadTimedOut(true);
            setIsLoadingElement(false);
            setMessage("Das Stripe-Zahlungsformular konnte nicht geladen werden. Bitte Seite neu laden oder Zahlungsmethode wechseln.");
            captureClientEvent("checkout_stripe_element_timeout", {
                payment_method: paymethod,
                total: price / 100,
            });
        }, 12000);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [isLoadingElement, paymethod, price]);

    const sendPayment = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            captureClientEvent("checkout_stripe_submit_blocked", {
                payment_method: paymethod,
                reason: "stripe_not_ready",
            });
            return;
        }
        captureClientEvent("checkout_stripe_submit_clicked", {
            payment_method: paymethod,
            total: price / 100,
        });
        setIsLoadingPayment(true);
        const baseUrl = process.env.NEXT_PUBLIC_URL ?? window.location.origin;
        const returnUrl = new URL(returnPath, baseUrl).toString();

        try {
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: returnUrl,
                    receipt_email: email,
                    payment_method_data: {
                        billing_details: {
                            email: email,
                            address: {
                                country: "DE",
                                postal_code: "18374",
                                state: "Mecklenburg-Vorpommern",
                                city: "Zingst",
                                line1: "Müggenburger Weg 45A",
                                line2: "18374 Zingst",
                            }
                        }
                    },
                },
            });

            if (error) {
                const errorMessage = error.message ?? "Ein unerwarteter Fehler ist aufgetreten.";
                if (error.type === "card_error" || error.type === "validation_error") {
                    setMessage(errorMessage);
                } else {
                    setMessage("Ein unerwarteter Fehler ist aufgetreten.");
                }
                captureClientEvent("checkout_stripe_confirm_failed", {
                    payment_method: paymethod,
                    total: price / 100,
                    reason: error.type,
                    error: errorMessage,
                });
                return;
            }

            captureClientEvent("checkout_stripe_confirm_submitted", {
                payment_method: paymethod,
                total: price / 100,
                return_url: returnUrl,
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            setMessage("Ein unerwarteter Fehler ist aufgetreten.");
            captureClientEvent("checkout_stripe_confirm_failed", {
                payment_method: paymethod,
                total: price / 100,
                reason: "exception",
                error: message,
            });
        } finally {
            setIsLoadingPayment(false);
        }
    };

    const paymentElementOptions: StripePaymentElementOptions = {
        layout: "accordion",
        fields: {
            billingDetails: {
                name: "auto",
                email: "never",
                address: "never",
            }
        }
    };

    return (
        <form id="payment-form" onSubmit={sendPayment} className="space-y-4">
            {isLoadingElement && (
                <div className="flex items-center justify-center py-8">
                    <FaSpinner className="h-6 w-6 animate-spin text-amber-500" />
                </div>
            )}
            
            <PaymentElement 
                id="payment-element" 
                options={paymentElementOptions} 
                onReady={() => {
                    setIsLoadingElement(false);
                    setElementLoadTimedOut(false);
                }}
                className="rounded-xl"
            />
            
            <button
                type="submit"
                disabled={isLoadingPayment || !stripe || !elements || elementLoadTimedOut}
                className="group relative mt-6 w-full overflow-hidden rounded-full bg-gradient-to-r from-slate-800 to-slate-900 py-4 font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
            >
                {isLoadingPayment ? (
                    <FaSpinner className="mx-auto h-5 w-5 animate-spin" />
                ) : (
                    <span className="flex items-center justify-center gap-2">
                        <FaLock className="h-4 w-4" />
                        Jetzt {formatCurrency(price)} bezahlen
                    </span>
                )}
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/0 via-white/10 to-white/0 transition-transform duration-500 group-hover:translate-x-full" />
            </button>
            
            {message && (
                <div className="rounded-xl bg-red-50 p-4 text-center text-sm text-red-600">
                    {message}
                </div>
            )}
        </form>
    );
}
